// client/src/utils/analyticsUtils.js

/**
 * Pure utility functions for analytics data formatting and display.
 * These functions have no side effects and depend only on their inputs.
 */

/**
 * Format a date string to localized date format
 * Date-only strings (YYYY-MM-DD) are interpreted in local timezone
 * to prevent off-by-one errors from UTC conversion
 * @param {string} dateString - ISO date string or YYYY-MM-DD date-only string
 * @returns {string} Formatted date
 */
export function formatDate(dateString) {
  if (
    typeof dateString === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateString)
  ) {
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day).toLocaleDateString()
  }

  return new Date(dateString).toLocaleDateString()
}

/**
 * Format a date string to localized date and time format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date and time
 */
export function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString()
}

/**
 * Get emoji icon for event type
 * @param {string} eventType - Type of telemetry event
 * @returns {string} Emoji representing the event type
 */
export function getEventTypeIcon(eventType) {
  switch (eventType) {
    case 'click':
      return '🖱️'
    case 'custom':
      return '⚡'
    case 'page_view':
      return '👁️'
    case 'error':
      return '❌'
    default:
      return '📊'
  }
}

/**
 * Categorize event tag based on naming conventions
 * @param {string} tag - Event tag string
 * @returns {string} Category name
 */
export function getTagCategory(tag) {
  if (tag.startsWith('navigation.')) return 'Navigation'
  if (tag.startsWith('match.')) return 'Match'
  if (tag.startsWith('admin.')) return 'Admin'
  if (tag.startsWith('user_')) return 'User'
  if (tag.includes('league')) return 'League'
  return 'Other'
}

/**
 * Get Bootstrap color class for tag category
 * @param {string} category - Category name from getTagCategory
 * @returns {string} Bootstrap color class (e.g., 'primary', 'success')
 */
export function getTagCategoryColor(category) {
  const colors = {
    Navigation: 'primary',
    Match: 'success',
    Admin: 'warning',
    User: 'info',
    League: 'secondary',
    Other: 'light',
  }
  return colors[category] || 'light'
}

/**
 * Get Bootstrap color class for health status
 * @param {string} status - Health status (e.g., 'ok', 'warning', 'error')
 * @returns {string} Bootstrap color class
 */
export function getHealthStatusColor(status) {
  switch (status) {
    case 'ok':
    case 'excellent':
      return 'success'
    case 'good':
      return 'info'
    case 'warning':
    case 'slow':
      return 'warning'
    case 'error':
    case 'unhealthy':
      return 'danger'
    default:
      return 'secondary'
  }
}

/**
 * Get emoji icon for health status
 * @param {string} status - Health status
 * @returns {string} Emoji representing the health status
 */
export function getHealthStatusIcon(status) {
  switch (status) {
    case 'ok':
    case 'excellent':
      return '✅'
    case 'good':
      return '🟢'
    case 'warning':
    case 'slow':
      return '⚠️'
    case 'error':
    case 'unhealthy':
      return '❌'
    default:
      return '⚪'
  }
}

/**
 * Split Sankey flow data into selected pages and remaining pages.
 * @param {Object|null} flowData - Sankey data with nodes and links
 * @param {string[]} pageNames - Page names to isolate into a separate flow
 * @returns {{selected: Object|null, remaining: Object|null}} Split flow data
 */
export function splitFlowDataByPages(flowData, pageNames = []) {
  if (!flowData || !Array.isArray(flowData.nodes)) {
    return { selected: flowData, remaining: flowData }
  }

  const normalizePageName = (name = '') => {
    if (!name) return ''
    const normalized = name.toString().trim().toLowerCase()
    const withoutHash = normalized.split('#')[0]
    const withoutQuery = withoutHash.split('?')[0]
    if (withoutQuery === '/') return withoutQuery
    return withoutQuery.replace(/\/+$/, '')
  }

  const pageSet = new Set(pageNames.map(normalizePageName))
  const selectedIndexes = new Set()

  flowData.nodes.forEach((node, index) => {
    if (pageSet.has(normalizePageName(node.name))) {
      selectedIndexes.add(index)
    }
  })

  const buildSubset = (includeSelected) => {
    const indexMap = new Map()
    const nodes = []

    flowData.nodes.forEach((node, index) => {
      const isSelected = selectedIndexes.has(index)
      if (
        (includeSelected && isSelected) ||
        (!includeSelected && !isSelected)
      ) {
        indexMap.set(index, nodes.length)
        nodes.push(node)
      }
    })

    const links = (flowData.links || [])
      .filter((link) => {
        const sourceSelected = selectedIndexes.has(link.source)
        const targetSelected = selectedIndexes.has(link.target)
        return includeSelected
          ? sourceSelected && targetSelected
          : !sourceSelected && !targetSelected
      })
      .map((link) => ({
        ...link,
        source: indexMap.get(link.source),
        target: indexMap.get(link.target),
      }))
      .filter((link) => link.source !== undefined && link.target !== undefined)

    return { ...flowData, nodes, links }
  }

  return {
    selected: buildSubset(true),
    remaining: buildSubset(false),
  }
}
